import { ReactElement, useMemo } from 'react';
import { useQuery } from 'react-query';

import { Button, IconButton } from '@raptormaps/button';
import { Table } from '@raptormaps/table';
import { Stack } from '@raptormaps/layout';
import { CircularProgress } from '@raptormaps/progress';
import { Text } from '@raptormaps/text';

import type { OrgUser } from '../../api/types/OrgUser';
import { getAllOrgPermissionsQuery } from '../../api/services/org-permissions';
import { RaptorSolarRolePicker } from './RaptorSolarRolePicker';

interface RaptorSolarRolesProps {
  orgId: number;
  users: OrgUser[];
  onRemoveUser: (user: OrgUser) => void;
  isLoading: boolean;
}

const columns = [
  { key: 'email', name: 'Email' },
  { key: 'role', name: 'Role' },
  { key: 'remove', name: 'Remove' },
];

export const RaptorSolarRoles = ({
  orgId,
  users,
  onRemoveUser,
  isLoading,
}: RaptorSolarRolesProps) => {
  const {
    data: orgPermissions,
    isLoading: isLoadingOrgPermissions,
    isFetching: isFetchingOrgPermissions,
    isError: isOrgPermissionsError,
    refetch: refetchOrgPermissions,
  } = useQuery(
    getAllOrgPermissionsQuery(orgId, { roles: ['admin', 'viewer'] }),
  );

  const tableData = useMemo(() => {
    if (!orgPermissions) {
      return [];
    }

    const orgUserPermissionsMap = new Map(
      orgPermissions.map(permission => [permission.user_id, permission]),
    );

    return users
      .reduce((acc, user) => {
        const userAuth0UserId = user.auth0UserId;
        if (!userAuth0UserId) {
          return acc;
        }

        const orgPermission = orgUserPermissionsMap.get(userAuth0UserId);

        acc.push({
          email: user.email ?? '',
          role: (
            <RaptorSolarRolePicker
              orgId={orgId}
              auth0UserId={userAuth0UserId}
              permissionId={orgPermission?.id ?? null}
              role={orgPermission?.role_id ?? null}
              isRefetching={isFetchingOrgPermissions}
            />
          ),
          remove: (
            <IconButton
              aria-label="Remove user"
              icon="TrashCan"
              onClick={() => onRemoveUser(user)}
              size="small"
              variant="tertiary"
            />
          ),
        });

        return acc;
      }, [] as Array<{ email: string; role: ReactElement; remove: ReactElement }>)
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [isFetchingOrgPermissions, onRemoveUser, orgId, orgPermissions, users]);

  // handle loading state here instead of the no-data-message to avoid rows being added to the table at various times
  if (isLoading || isLoadingOrgPermissions) {
    return (
      <Stack align="center" justify="center" style={{ padding: 24 }}>
        <CircularProgress size={75} />
      </Stack>
    );
  }

  return (
    <div style={{ margin: '20px 0px' }}>
      <Table
        cols={columns}
        data={tableData}
        totalRowCount={1}
        fetchData={() => Promise.resolve()}
        hideControls
        noDataMessage={
          isOrgPermissionsError ? (
            <Stack align="center" style={{ paddingBottom: '2rem' }}>
              <Text variant="paragraph_large" align="center">
                Error loading data.
              </Text>
              <Button onClick={() => void refetchOrgPermissions()}>
                Retry
              </Button>
            </Stack>
          ) : null
        }
      />
    </div>
  );
};
